import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, ShieldCheck, Search } from "lucide-react";
import Layout from "@/components/Layout";
import EmailListEditor from "@/components/EmailListEditor";
import SiteRoleCard from "@/components/SiteRoleCard";
import AuditLogDialog from "@/components/AuditLogDialog";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
import GuidedTour from "@/components/GuidedTour";
import { useAuth } from "@/contexts/AuthContext";
import { canManageUsers } from "@/lib/permissions";
import { consumePendingTour } from "@/lib/tour";
import { TOUR_STEPS } from "@/lib/tourSteps";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";

export default function UserManagement() {
  const { user } = useAuth();
  const isNationalAdmin = user?.permissions?.role === "national_admin";
  const allowed = canManageUsers(user);
  const mySites = user?.permissions?.sites || [];
  const [sites, setSites] = useState([]);
  const [nationalAdmins, setNationalAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newService, setNewService] = useState("");
  const [newSite, setNewSite] = useState(isNationalAdmin ? "" : mySites[0] || "");
  const [toDelete, setToDelete] = useState(null);
  const [search, setSearch] = useState("");
  const [runTour, setRunTour] = useState(false);

  useEffect(() => {
    if (consumePendingTour("site_admin")) setRunTour(true);
  }, []);

  useEffect(() => {
    if (!allowed) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get("/users/sites");
        setSites(res.data);
        if (isNationalAdmin) {
          const na = await api.get("/users/national-admins");
          setNationalAdmins(na.data.emails);
        }
      } catch (e) {
        toast.error("Impossible de charger la gestion des utilisateurs");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [allowed, isNationalAdmin]);

  const handleUpdateField = async (key, field, emails) => {
    try {
      const res = await api.put(`/users/sites/${key}`, { [field]: emails });
      setSites((prev) => prev.map((s) => (s.key === key ? res.data : s)));
      toast.success("Mis à jour");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la mise à jour");
    }
  };

  const handleAddSite = async () => {
    if (!newSite.trim()) {
      toast.error("Le site est obligatoire");
      return;
    }
    try {
      const res = await api.post("/users/sites", { service: newService.trim(), site: newSite.trim() });
      setSites((prev) => [...prev, res.data]);
      setNewService("");
      setNewSite(isNationalAdmin ? "" : mySites[0] || "");
      toast.success(isNationalAdmin ? "Site ajouté" : "Service ajouté");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'ajout");
    }
  };

  const handleDeleteSite = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/users/sites/${toDelete}`);
      setSites((prev) => prev.filter((s) => s.key !== toDelete));
      toast.success("Site supprimé");
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setToDelete(null);
    }
  };

  const handleUpdateNationalAdmins = async (emails) => {
    try {
      const res = await api.put("/users/national-admins", { emails });
      setNationalAdmins(res.data.emails);
      toast.success("Administrateurs nationaux mis à jour");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la mise à jour");
    }
  };

  if (!allowed) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="user-management-access-denied">
          <p className="text-muted-foreground">Vous n'avez pas les droits pour accéder à cette page.</p>
        </div>
      </Layout>
    );
  }

  const filteredSites = sites.filter((s) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return s.site.toLowerCase().includes(q) || (s.service || "").toLowerCase().includes(q);
  });

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="user-management-page">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-semibold text-foreground">Gestion des utilisateurs</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Gérez les rôles et droits d'accès par site, synchronisés en direct avec Google Sheets
            </p>
          </div>
          {isNationalAdmin && <AuditLogDialog />}
        </div>

        {isNationalAdmin && (
          <div className="bg-card border border-border rounded-lg p-5 mb-6" data-testid="national-admins-section">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <h2 className="font-heading text-lg font-semibold text-foreground">Administrateurs Nationaux</h2>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Accès complet à tous les sites et toutes les SOP.</p>
            <EmailListEditor emails={nationalAdmins} onChange={handleUpdateNationalAdmins} testIdPrefix="national-admins" />
          </div>
        )}

        {(isNationalAdmin || mySites.length > 0) && (
          <div className="bg-card border border-border rounded-lg p-5 mb-6" data-testid="add-site-section">
            <h2 className="font-heading text-lg font-semibold text-foreground mb-3">
              {isNationalAdmin ? "Ajouter un site" : "Ajouter un service"}
            </h2>
            {!isNationalAdmin && (
              <p className="text-xs text-muted-foreground mb-3">
                Vous pouvez ajouter un nouveau service pour l'un de vos propres sites.
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                placeholder="Service (ex: Fabrication)"
                value={newService}
                onChange={(e) => setNewService(e.target.value)}
                data-testid="new-site-service-input"
              />
              {isNationalAdmin ? (
                <Input
                  placeholder="Code site (ex: CON)"
                  value={newSite}
                  onChange={(e) => setNewSite(e.target.value)}
                  data-testid="new-site-code-input"
                />
              ) : (
                <Select value={newSite} onValueChange={setNewSite}>
                  <SelectTrigger className="sm:w-48" data-testid="new-site-code-select">
                    <SelectValue placeholder="Choisir un site" />
                  </SelectTrigger>
                  <SelectContent>
                    {mySites.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`new-site-code-option-${s}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button onClick={handleAddSite} data-testid="add-site-button">
                <Plus className="h-4 w-4" />
                Ajouter
              </Button>
            </div>
          </div>
        )}

        {loading && <p className="text-sm text-muted-foreground text-center py-8">Chargement...</p>}
        {!loading && sites.length > 0 && (
          <div className="relative mb-4 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par site ou service..."
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="site-search-input"
            />
          </div>
        )}
        {!loading && sites.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8" data-testid="no-sites-message">
            Aucun site trouvé.
          </p>
        )}
        {!loading && sites.length > 0 && filteredSites.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8" data-testid="no-search-results-message">
            Aucun site ne correspond à votre recherche.
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredSites.map((row) => (
            <SiteRoleCard
              key={row.key}
              row={row}
              isNationalAdmin={isNationalAdmin}
              onUpdateField={handleUpdateField}
              onDelete={(key) => setToDelete(key)}
            />
          ))}
        </div>
      </div>

      <DeleteConfirmDialog
        open={!!toDelete}
        onOpenChange={(v) => !v && setToDelete(null)}
        onConfirm={handleDeleteSite}
        title="Supprimer ce site ?"
        description="Ce site et tous ses rôles associés seront définitivement supprimés de Google Sheets."
      />
      <GuidedTour steps={TOUR_STEPS.site_admin} run={runTour} onFinish={() => setRunTour(false)} />
    </Layout>
  );
}
